/**
 * TaskForm Component
 * 
 * A modal form for creating and editing Tasks/Notes in the TriSys system.
 * Based on specifications in specifications/Task-Component.md
 * 
 * Uses <trisys-field> components and follows Apex visual patterns.
 * 
 * @module TaskForm
 */

import { ModalSystem } from '@/services/ModalSystem';
import { ToasterService } from '@/services/ToasterService';
import { Logger } from '@/services/Logger';
import { trisysAPI, EntitySkill } from '@/services/api/TriSysWebAPI';
import { ContactPicker } from '@/components/ContactPicker';
import { UserPicker } from '@/components/UserPicker';
import { AttributesAndSkills, SkillItem } from '@/components/AttributesAndSkills';
import { DocumentViewer } from '@/components/DocumentViewer';
import { 
  renderTrisysFields, 
  createComponentRegistry, 
  destroyComponentRegistry,
  ComponentRegistry 
} from '@/tenants/trisys-2026-01-13/utils/fieldMapping';

const logger = Logger.scope('TaskForm');

/**
 * Task data interface - matches API response from /Task/Read
 */
export interface ITask {
  TaskId: number;
  TaskType: string;           // API uses TaskType string, not TaskTypeId
  TaskTypeId?: number;        // Optional for backwards compatibility
  TaskTypeName?: string;      // Legacy name
  TaskTypeImage?: string;
  Description: string;
  DisplayDescription?: string;
  StartDate: string;          // API format: "2026/01/29 10:40"
  EndDate: string;            // API format: "2026/01/29 10:50"
  StartDateTime?: string;     // Legacy ISO format
  EndDateTime?: string;       // Legacy ISO format
  Scheduled: boolean;         // API uses "Scheduled", not "IsScheduled"
  IsScheduled?: boolean;      // Legacy name
  IsAllDayTask?: boolean;
  Alarmed: boolean;           // API uses "Alarmed", not "HasAlarm"
  HasAlarm?: boolean;         // Legacy name
  LeadTime?: string;          // API format: "5 Minutes"
  AlarmLeadTime?: number;     // Legacy format
  AlarmLeadTimePeriod?: string; // Legacy format
  AttachmentFilePath?: string;
  AttachmentDescription?: string;
  OwnerUserId: number;
  LoginName?: string;
  Contacts?: ITaskContact[];
  Users?: ITaskUser[];
  Links?: ITaskLink[];
  Skills?: unknown;
  Attachments?: ITaskAttachment[];
  Attributes?: ITaskAttribute[];
}

/**
 * Task contact from API - full contact details
 */
interface ITaskContact {
  ContactId: number;
  FullName: string;
  Forenames?: string;
  Surname?: string;
  Title?: string;
  ContactType: string;
  JobTitle?: string;
  CompanyName?: string;
  CompanyId?: number;
  ContactPhotoURL?: string;
  ContactPhoto?: string;
  HomeAddressTelNo?: string;
  MobileTelNo?: string;
  WorkTelNo?: string;
  WorkMobileTelNo?: string;
  PersonalEMail?: string;
  WorkEMail?: string;
  Status?: string;
  Priority?: string;
}

interface ITaskUser {
  UserId: number;
  FullName: string;
  LoginName: string;
  IsOwner?: boolean;
  Avatar?: string | null;
}

interface ITaskLink {
  Name: string;
  RecordId: number;
  Description: string;
}

interface ITaskAttribute {
  AttributeId: number;
  AttributeName: string;
  AttributeValue: string;
}

/**
 * Task attachment from API
 */
interface ITaskAttachment {
  DocumentPath: string;
  Description: string;
}

/**
 * Task type for dropdown
 */
interface ITaskType {
  TaskTypeId: number;
  Name: string;
}

/**
 * Options for opening the TaskForm
 */
export interface ITaskFormOptions {
  /** Task ID to edit (0 or undefined for new task) */
  taskId?: number;
  /** Whether this is a scheduled task (true) or note (false) */
  scheduled?: boolean;
  /** Default contact ID to link */
  defaultContactId?: number;
  /** Default contact name (used with defaultContactId for display) */
  defaultContactName?: string;
  /** Default contact photo URL (used with defaultContactId for avatar) */
  defaultContactPhotoUrl?: string;
  /** Default owner user ID (defaults to current logged-in user) */
  defaultUserId?: number;
  /** Default owner user name (used with defaultUserId for display) */
  defaultUserName?: string;
  /** Callback when task is saved */
  onSave?: (task: ITask) => void;
  /** Callback when task is deleted */
  onDelete?: (taskId: number) => void;
  /** Callback when form closes */
  onClose?: () => void;
}

/**
 * TaskForm - Modal form for creating/editing tasks
 */
export class TaskForm {
  private static modalId: string | null = null;
  private static currentTask: ITask | null = null;
  private static options: ITaskFormOptions = {};
  private static isDirty: boolean = false;
  private static componentRegistry: ComponentRegistry | null = null;
  private static attributesAndSkills: AttributesAndSkills | null = null;

  /**
   * Show the task form modal
   */
  public static async show(options: ITaskFormOptions = {}): Promise<void> {
    this.options = options;
    this.isDirty = false;
    
    const isEdit = options.taskId && options.taskId > 0;
    const titleText = options.scheduled !== false ? 'Scheduled Task' : 'Note/History';
    const titleWithId = isEdit ? `${titleText} (${options.taskId?.toLocaleString()})` : `New ${titleText}`;
    const title = `<i class="fas fa-external-link-alt text-base-content/50 mr-2 cursor-pointer" title="Open in new window"></i>${titleWithId}`;

    // Show loading modal
    const waitModalId = ModalSystem.showWait(isEdit ? 'Loading task...' : 'Preparing form...');

    try {
      // Load task data if editing
      if (isEdit && options.taskId) {
        await this.loadTask(options.taskId);
      } else {
        this.currentTask = this.createNewTask(options.scheduled !== false);
      }

      ModalSystem.hide(waitModalId);

      // Build and show the form
      const html = this.buildFormHtml();
      
      // Build buttons
      const buttons: Array<{ caption: string; className?: string; callback: () => void | boolean | Promise<void | boolean> }> = [
        { 
          caption: 'Delete', 
          className: 'btn-error btn-outline', 
          callback: () => { this.handleDelete(); return false; }
        },
        { 
          caption: 'Save & Close', 
          className: 'btn-primary', 
          callback: () => { this.handleSave(true); return false; }
        },
        { 
          caption: 'Save & Clear', 
          className: 'btn-secondary', 
          callback: () => { this.handleSaveAndClear(); return false; }
        },
        { 
          caption: 'Follow-Up', 
          className: 'btn-info', 
          callback: () => { this.handleFollowUp(); return false; }
        },
        { 
          caption: 'Cancel & Close', 
          className: 'btn-ghost', 
          callback: () => { this.handleClose(); return false; }
        }
      ];
      
      // Hide delete button for new tasks
      if (!isEdit) {
        buttons.shift(); // Remove delete button
      }
      
      this.modalId = ModalSystem.show({
        title,
        html,
        width: 'w-full md:w-[50vw] max-w-[95vw]',
        buttons
      });

      // Initialize form after modal is shown
      setTimeout(() => this.initializeForm(), 100);

    } catch (error) {
      ModalSystem.hide(waitModalId);
      logger.error('Failed to open task form', { error, options });
      ToasterService.error('Failed to open task form');
    }
  }

  /**
   * Load existing task from API
   */
  private static async loadTask(taskId: number): Promise<void> {
    try {
      const response = await trisysAPI.request<{ Success: boolean; Task: ITask; ErrorMessage?: string }>('/Task/Read', { TaskId: taskId });
      if (response.Success && response.Task) {
        this.currentTask = response.Task;
        
        // Enrich users with photos from standing data
        this.enrichUsersWithPhotos();
        
        logger.debug('Task loaded', { taskId, task: this.currentTask });
      } else {
        throw new Error(response.ErrorMessage || 'Task not found');
      }
    } catch (error) {
      logger.error('Failed to load task', { taskId, error });
      throw error;
    }
  }
  
  /**
   * Enrich task users with photos from standing data
   * The API may not return photos, but they are available in cached standing data
   */
  private static enrichUsersWithPhotos(): void {
    if (!this.currentTask?.Users?.length) return;
    
    const standingData = trisysAPI.getCachedStandingData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const standingDataUsers = (standingData as any)?.Users as Array<{ UserId: number; Photo?: string }> || [];
    
    // Create a map for quick lookup
    const userPhotoMap = new Map<number, string>();
    for (const user of standingDataUsers) {
      if (user.Photo) {
        userPhotoMap.set(user.UserId, user.Photo);
      }
    }
    
    // Update each user's Avatar if not already set
    for (const user of this.currentTask.Users) {
      if (!user.Avatar && userPhotoMap.has(user.UserId)) {
        user.Avatar = userPhotoMap.get(user.UserId) || null;
      }
    }
    
    logger.debug('Users enriched with photos from standing data', { 
      userCount: this.currentTask.Users.length,
      photosAdded: this.currentTask.Users.filter(u => u.Avatar).length
    });
  }

  /**
   * Create a new task with defaults
   * Uses options for default contact and user if provided
   */
  private static createNewTask(scheduled: boolean): ITask {
    const now = new Date();
    
    // For scheduled tasks: round start time to 10 minutes from now, at a 5-minute boundary
    // For notes: use current time
    let startTime: Date;
    let endTime: Date;
    
    if (scheduled) {
      // Add 10 minutes to current time
      const futureTime = new Date(now.getTime() + 10 * 60 * 1000);
      // Round up to nearest 5-minute interval
      const minutes = futureTime.getMinutes();
      const remainder = minutes % 5;
      if (remainder > 0) {
        futureTime.setMinutes(minutes + (5 - remainder));
      }
      futureTime.setSeconds(0);
      futureTime.setMilliseconds(0);
      startTime = futureTime;
      endTime = new Date(startTime.getTime() + 5 * 60 * 1000); // 5 minutes after start
    } else {
      startTime = now;
      endTime = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes later
    }
    
    const taskTypeName = scheduled ? 'ToDo' : 'Note';
    
    // Look up TaskTypeId from standing data by name (don't hardcode IDs)
    const standingData = trisysAPI.getCachedStandingData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTypes: ITaskType[] = (standingData as any)?.TaskTypes || [];
    const matchingType = taskTypes.find(tt => tt.Name === taskTypeName);
    const taskTypeId = matchingType?.TaskTypeId ?? 0;
    
    // Get default user from options or current logged-in user
    const cachedProfile = trisysAPI.getCachedProfile();
    const defaultUserId = this.options.defaultUserId ?? cachedProfile?.userId ?? 0;
    const defaultUserName = this.options.defaultUserName ?? cachedProfile?.displayName ?? '';
    const defaultLoginName = cachedProfile?.loginName ?? '';
    
    // Get user photo from cached profile or standing data
    let defaultUserAvatar = cachedProfile?.avatar || null;
    if (!defaultUserAvatar && defaultUserId) {
      // Fallback: look up photo from standing data Users array
      const standingData = trisysAPI.getCachedStandingData();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const standingDataUsers = (standingData as any)?.Users as Array<{ UserId: number; Photo?: string }> || [];
      const standingDataUser = standingDataUsers.find(u => u.UserId === defaultUserId);
      defaultUserAvatar = standingDataUser?.Photo || null;
    }
    
    // Build contacts array from default contact if provided
    const contacts: ITaskContact[] = [];
    if (this.options.defaultContactId) {
      contacts.push({
        ContactId: this.options.defaultContactId,
        FullName: this.options.defaultContactName || '',
        ContactType: 'Contact',
        ContactPhotoURL: this.options.defaultContactPhotoUrl
      });
    }
    
    // Build users array with default user as owner
    const users: ITaskUser[] = [];
    if (defaultUserId) {
      users.push({
        UserId: defaultUserId,
        FullName: defaultUserName,
        LoginName: defaultLoginName,
        IsOwner: true,
        Avatar: defaultUserAvatar
      });
    }
    
    return {
      TaskId: 0,
      TaskType: taskTypeName,
      TaskTypeId: taskTypeId,
      TaskTypeName: taskTypeName,
      Description: '',
      StartDate: this.formatApiDateTime(startTime),
      EndDate: this.formatApiDateTime(endTime),
      StartDateTime: startTime.toISOString(),
      EndDateTime: endTime.toISOString(),
      Scheduled: scheduled,
      IsScheduled: scheduled,
      IsAllDayTask: false,
      Alarmed: false,
      HasAlarm: false,
      OwnerUserId: defaultUserId,
      Contacts: contacts,
      Users: users,
      Links: [],
      Attributes: []
    };
  }

  /**
   * Get TaskType icon URL
   */
  private static getTaskTypeIconUrl(taskTypeName: string): string {
    if (!taskTypeName) return '';
    const encodedName = encodeURIComponent(taskTypeName);
    return `https://apex.trisys.co.uk/images/trisys/16x16/tasktype/${encodedName}.png`;
  }

  /**
   * Parse LeadTime from API format (e.g., "5 Minutes") or legacy format
   * Returns { value: number, period: string }
   */
  private static parseLeadTime(task: ITask): { value: number; period: string } {
    // Try new API format first: "5 Minutes"
    if (task.LeadTime) {
      const match = task.LeadTime.match(/^(\d+)\s+(.+)$/);
      if (match) {
        const [, valueStr, periodStr] = match;
        // Normalize period to match select options (singular, title case)
        const period = periodStr.replace(/s$/i, ''); // Remove trailing 's'
        const normalizedPeriod = period.charAt(0).toUpperCase() + period.slice(1).toLowerCase();
        return { value: parseInt(valueStr, 10), period: normalizedPeriod };
      }
    }
    // Fall back to legacy format
    return {
      value: task.AlarmLeadTime || 15,
      period: task.AlarmLeadTimePeriod || 'Minutes'
    };
  }

  /**
   * Build the form HTML matching Apex layout
   */
  private static buildFormHtml(): string {
    const task = this.currentTask!;
    
    // Parse lead time from API format or legacy format
    const leadTime = this.parseLeadTime(task);
    
    // Get TaskTypes from standing data
    const standingData = trisysAPI.getCachedStandingData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const taskTypes: ITaskType[] = (standingData as any)?.TaskTypes || [];
    
    // Build TaskType options with icons
    // API returns TaskType as string (e.g., "Interim"), match by name
    const taskTypeOptions = taskTypes
      .sort((a, b) => a.Name.localeCompare(b.Name))
      .map(tt => {
        const iconUrl = this.getTaskTypeIconUrl(tt.Name);
        // Match by TaskType string (API field) or TaskTypeName or TaskTypeId
        const isSelected = task.TaskType === tt.Name || task.TaskTypeName === tt.Name || task.TaskTypeId === tt.TaskTypeId;
        const selected = isSelected ? 'selected' : '';
        return `<option value="${tt.TaskTypeId}" data-icon="${iconUrl}" data-name="${this.escapeHtml(tt.Name)}" ${selected}>${this.escapeHtml(tt.Name)}</option>`;
      })
      .join('');
    
    // Get current TaskType icon - use TaskType string from API
    const currentTaskTypeName = task.TaskType || task.TaskTypeName || '';

    return `
      <div id="task-form-content" class="flex flex-col gap-4">
        
        <!-- Form Fields Section - styled like Apex -->
        <div class="card bg-base-100 border border-base-300 shadow-sm">
          <div class="card-body p-4">
            
            <!-- Row 1: Task Type + Scheduled toggle -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text font-medium">Task Type</span>
                </label>
                <div class="flex items-center gap-2">
                  <img id="task-type-icon" 
                       src="${this.getTaskTypeIconUrl(currentTaskTypeName)}" 
                       class="w-5 h-5" 
                       onerror="this.style.display='none'" />
                  <select id="task-type-select" class="select select-bordered select-sm flex-1">
                    ${taskTypeOptions}
                  </select>
                </div>
              </div>
              <div class="form-control flex items-end justify-end">
                <label class="label cursor-pointer gap-3">
                  <input type="checkbox" id="task-scheduled" class="toggle toggle-primary" ${task.IsScheduled ? 'checked' : ''}>
                  <span class="label-text font-medium">Scheduled</span>
                </label>
              </div>
            </div>
            
            <!-- Row 2: Start Date + End Date (equal widths) -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div class="form-control">
                <trisys-field id="task-start-date" 
                              data-type="Date" 
                              show-time="true"
                              show-day="true"
                              minute-interval="5"
                              label="Start Date / Time"></trisys-field>
              </div>
              <div class="form-control" id="end-date-container" ${task.IsAllDayTask ? 'style="display: none;"' : ''}>
                <trisys-field id="task-end-date" 
                              data-type="Date" 
                              show-time="true"
                              show-day="true"
                              minute-interval="5"
                              label="End Date / Time"></trisys-field>
              </div>
            </div>
            
            <!-- Row 3: All Day Task toggle (right aligned) -->
            <div class="flex justify-end">
              <div class="form-control">
                <label class="label cursor-pointer gap-3">
                  <input type="checkbox" id="task-allday" class="toggle toggle-secondary" ${task.IsAllDayTask ? 'checked' : ''}>
                  <span class="label-text font-medium">All Day Task</span>
                </label>
              </div>
            </div>
            
          </div>
        </div>
        
        <!-- Tabbed Content Section -->
        <div class="border border-base-300 rounded-lg overflow-hidden">
          
          <!-- Tab Navigation - tabs-lift style like contact form -->
          <div role="tablist" class="tabs tabs-lift flex-nowrap w-full bg-base-200" id="task-form-tabs">
            <a role="tab" class="tab tab-active cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="description">
              <span class="tab-content"><i class="fas fa-align-left mr-1"></i><span>Description</span></span>
            </a>
            <a role="tab" class="tab cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="alarm">
              <span class="tab-content"><i class="fas fa-bell mr-1"></i><span>Alarm</span></span>
            </a>
            <a role="tab" class="tab cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="contacts">
              <span class="tab-content"><i class="fas fa-users mr-1"></i><span>Contacts</span></span>
            </a>
            <a role="tab" class="tab cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="users">
              <span class="tab-content"><i class="fas fa-user-friends mr-1"></i><span>Users</span></span>
            </a>
            <a role="tab" class="tab cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="attachment">
              <span class="tab-content"><i class="fas fa-paperclip mr-1"></i><span>Attachment</span></span>
            </a>
            <a role="tab" class="tab cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="links">
              <span class="tab-content"><i class="fas fa-link mr-1"></i><span>Links</span></span>
            </a>
            <a role="tab" class="tab cursor-pointer whitespace-nowrap task-tab flex-1 justify-center" data-tab="attributes">
              <span class="tab-content"><i class="fas fa-tags mr-1"></i><span>Attributes</span></span>
            </a>
          </div>
            
          <!-- Tab Panels - scrolling happens WITHIN each panel, not at form level -->
          <div class="p-4 bg-base-100 max-h-[50vh] overflow-hidden">
              
              <!-- Description Tab -->
              <div id="panel-description" class="task-tab-panel h-full overflow-y-auto entity-form-scrollable">
                <textarea id="task-description" 
                          class="textarea textarea-bordered w-full h-[200px] text-sm" 
                          placeholder="Enter notes or description...">${this.escapeHtml(task.Description || '')}</textarea>
              </div>
              
              <!-- Alarm Tab -->
              <div id="panel-alarm" class="task-tab-panel hidden h-full overflow-y-auto entity-form-scrollable">
                <div class="card bg-base-100 border border-base-300">
                  <div class="card-body p-4">
                    <div class="flex flex-col gap-4">
                      <!-- Set Alarm toggle -->
                      <div class="form-control">
                        <label class="label cursor-pointer justify-start gap-3">
                          <span class="label-text font-medium">Set Alarm</span>
                          <input type="checkbox" id="task-has-alarm" class="toggle toggle-primary" ${task.HasAlarm ? 'checked' : ''}>
                        </label>
                      </div>
                      <!-- Lead Time controls - only visible when alarm is set -->
                      <div id="alarm-lead-time" class="flex flex-wrap items-center gap-3 ${task.HasAlarm ? '' : 'hidden'}">
                        <span class="label-text font-medium">Lead Time</span>
                        <input type="number" id="task-alarm-value" 
                               class="input input-bordered input-sm w-24 text-center" 
                               value="${leadTime.value}" min="1">
                        <select id="task-alarm-period" class="select select-bordered select-sm w-32">
                          <option value="Minutes" ${leadTime.period === 'Minutes' || leadTime.period === 'Minute' ? 'selected' : ''}>minutes</option>
                          <option value="Hours" ${leadTime.period === 'Hours' || leadTime.period === 'Hour' ? 'selected' : ''}>hours</option>
                          <option value="Days" ${leadTime.period === 'Days' || leadTime.period === 'Day' ? 'selected' : ''}>days</option>
                          <option value="Weeks" ${leadTime.period === 'Weeks' || leadTime.period === 'Week' ? 'selected' : ''}>weeks</option>
                          <option value="Months" ${leadTime.period === 'Months' || leadTime.period === 'Month' ? 'selected' : ''}>months</option>
                          <option value="Years" ${leadTime.period === 'Years' || leadTime.period === 'Year' ? 'selected' : ''}>years</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Contacts Tab -->
              <div id="panel-contacts" class="task-tab-panel hidden h-full overflow-y-auto entity-form-scrollable">
                <div class="card bg-base-100 border border-base-300">
                  <div class="card-body p-4">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-sm font-medium" id="contacts-count">${task.Contacts?.length || 0} Task Contact${(task.Contacts?.length || 0) !== 1 ? 's' : ''}</span>
                      <button id="btn-add-contact" class="btn btn-sm btn-primary btn-outline">
                        <i class="fas fa-plus mr-1"></i>Add
                      </button>
                    </div>
                    <div id="task-contacts-list" class="space-y-3 max-h-[300px] overflow-y-auto entity-form-scrollable">
                      ${this.renderContactsList(task.Contacts || [])}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Users Tab -->
              <div id="panel-users" class="task-tab-panel hidden h-full overflow-y-auto entity-form-scrollable">
                <div class="card bg-base-100 border border-base-300">
                  <div class="card-body p-4">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-sm font-medium" id="users-count">${task.Users?.length || 0} Task User${(task.Users?.length || 0) !== 1 ? 's' : ''}</span>
                      <button id="btn-add-user" class="btn btn-sm btn-primary btn-outline">
                        <i class="fas fa-plus mr-1"></i>Add
                      </button>
                    </div>
                    <div id="task-users-list" class="space-y-2 max-h-[300px] overflow-y-auto entity-form-scrollable">
                      ${this.renderUsersList(task.Users || [], task.OwnerUserId)}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Attachment Tab -->
              <div id="panel-attachment" class="task-tab-panel hidden h-full overflow-y-auto entity-form-scrollable">
                <div class="card bg-base-100 border border-base-300">
                  <div class="card-body p-4 space-y-4">
                    <!-- File Reference field using trisys-field -->
                    <trisys-field id="task-attachment-file" 
                                  data-type="File Reference" 
                                  label="Attached Document"></trisys-field>
                    
                    <!-- Description textarea -->
                    <div class="form-control w-full">
                      <label class="label py-1">
                        <span class="label-text text-sm font-medium">Description</span>
                      </label>
                      <textarea id="task-attachment-description" 
                                class="textarea textarea-bordered textarea-sm w-full" 
                                rows="3"
                                placeholder="Optional description of the attachment">${this.escapeHtml(task.AttachmentDescription || '')}</textarea>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Links Tab -->
              <div id="panel-links" class="task-tab-panel hidden h-full overflow-y-auto entity-form-scrollable">
                <div class="card bg-base-100 border border-base-300">
                  <div class="card-body p-4">
                    <div class="flex items-center justify-between mb-3">
                      <span class="text-sm font-medium" id="links-count">${task.Links?.length || 0} Task Link${(task.Links?.length || 0) !== 1 ? 's' : ''}</span>
                      <div class="dropdown dropdown-end">
                        <label tabindex="0" class="btn btn-sm btn-primary btn-outline">
                          <i class="fas fa-link mr-1"></i>Link <i class="fas fa-caret-down ml-1"></i>
                        </label>
                        <ul tabindex="0" class="dropdown-content flex flex-col p-2 shadow bg-base-100 rounded-box z-50" 
                            style="max-height: 70vh; overflow-y: auto; width: max-content; min-width: 200px;">
                          <li class="list-none"><a class="flex items-center gap-2 px-3 py-2 hover:bg-base-200 rounded cursor-pointer whitespace-nowrap" data-link-type="Requirement"><i class="fas fa-briefcase w-4"></i>Link Requirement</a></li>
                          <li class="list-none"><a class="flex items-center gap-2 px-3 py-2 hover:bg-base-200 rounded cursor-pointer whitespace-nowrap" data-link-type="Placement"><i class="fas fa-user-check w-4"></i>Link Placement</a></li>
                          <li class="list-none"><a class="flex items-center gap-2 px-3 py-2 hover:bg-base-200 rounded cursor-pointer whitespace-nowrap" data-link-type="Timesheet"><i class="fas fa-clock w-4"></i>Link Timesheet</a></li>
                        </ul>
                      </div>
                    </div>
                    <div id="task-links-list" class="space-y-3 max-h-[300px] overflow-y-auto entity-form-scrollable">
                      ${this.renderLinksList(task.Links || [])}
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Attributes Tab -->
              <div id="panel-attributes" class="task-tab-panel hidden h-full overflow-y-auto entity-form-scrollable">
                <!-- AttributesAndSkills component container -->
                <div id="task-attributes-container">
                  <div class="flex items-center justify-center h-48">
                    <span class="loading loading-spinner loading-md"></span>
                    <span class="ml-2 text-base-content/60">Loading attributes...</span>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        
      </div>
    `;
  }

  /**
   * Render contacts list HTML - matches Apex layout with avatar, contact details and phone/email
   */
  private static renderContactsList(contacts: ITaskContact[]): string {
    if (contacts.length === 0) {
      return '<p class="text-base-content/50 text-sm">No contacts linked to this task.</p>';
    }
    
    return contacts.map(c => {
      // Build phone/email rows
      const contactDetails: string[] = [];
      
      if (c.HomeAddressTelNo) {
        contactDetails.push(`
          <div class="flex items-center gap-3 py-2 border-t border-base-300">
            <i class="fas fa-phone text-success w-4"></i>
            <div class="flex-1">
              <div class="text-sm font-medium">Home Phone</div>
              <a href="tel:${this.escapeHtml(c.HomeAddressTelNo)}" class="text-sm text-primary">${this.escapeHtml(c.HomeAddressTelNo)}</a>
            </div>
          </div>
        `);
      }
      
      if (c.MobileTelNo) {
        contactDetails.push(`
          <div class="flex items-center gap-3 py-2 border-t border-base-300">
            <i class="fas fa-mobile-alt text-base-content/50 w-4"></i>
            <div class="flex-1">
              <div class="text-sm font-medium">Personal Mobile</div>
              <a href="tel:${this.escapeHtml(c.MobileTelNo)}" class="text-sm text-primary">${this.escapeHtml(c.MobileTelNo)}</a>
            </div>
          </div>
        `);
      }
      
      if (c.WorkTelNo) {
        contactDetails.push(`
          <div class="flex items-center gap-3 py-2 border-t border-base-300">
            <i class="fas fa-phone-office text-base-content/50 w-4"></i>
            <div class="flex-1">
              <div class="text-sm font-medium">Work Phone</div>
              <a href="tel:${this.escapeHtml(c.WorkTelNo)}" class="text-sm text-primary">${this.escapeHtml(c.WorkTelNo)}</a>
            </div>
          </div>
        `);
      }
      
      if (c.PersonalEMail && c.PersonalEMail !== 'none supplied') {
        contactDetails.push(`
          <div class="flex items-center gap-3 py-2 border-t border-base-300">
            <i class="fas fa-envelope text-info w-4"></i>
            <div class="flex-1">
              <div class="text-sm font-medium">Personal E-Mail</div>
              <a href="mailto:${this.escapeHtml(c.PersonalEMail)}" class="text-sm text-primary">${this.escapeHtml(c.PersonalEMail)}</a>
            </div>
          </div>
        `);
      }
      
      if (c.WorkEMail && c.WorkEMail !== 'none supplied') {
        contactDetails.push(`
          <div class="flex items-center gap-3 py-2 border-t border-base-300">
            <i class="fas fa-envelope-open text-base-content/50 w-4"></i>
            <div class="flex-1">
              <div class="text-sm font-medium">Work E-Mail</div>
              <a href="mailto:${this.escapeHtml(c.WorkEMail)}" class="text-sm text-primary">${this.escapeHtml(c.WorkEMail)}</a>
            </div>
          </div>
        `);
      }
      
      // Avatar: use photo if available, otherwise initials
      const avatarHtml = c.ContactPhotoURL 
        ? `<img src="${c.ContactPhotoURL}" alt="${this.escapeHtml(c.FullName)}" class="rounded-full w-12 h-12 object-cover" onerror="this.parentElement.innerHTML='<span class=\\'text-lg\\'>${this.getInitials(c.FullName)}</span>'" />`
        : `<span class="text-lg">${this.getInitials(c.FullName)}</span>`;
      
      return `
        <div class="card bg-base-100 border border-base-300 rounded-lg overflow-hidden">
          <!-- Header row with avatar, name, type, job title and remove button -->
          <div class="flex items-start gap-3 p-3">
            <div class="avatar placeholder flex-shrink-0">
              <div class="bg-neutral text-neutral-content rounded-full w-12 h-12 flex items-center justify-center">
                ${avatarHtml}
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-baseline gap-2">
                <span class="font-medium text-primary">${this.escapeHtml(c.FullName)}</span>
                <span class="text-base-content/50">|</span>
                <span class="text-sm text-base-content/70">${this.escapeHtml(c.ContactType)}</span>
              </div>
              ${c.JobTitle ? `<div class="text-sm text-base-content/70">${this.escapeHtml(c.JobTitle)}</div>` : ''}
            </div>
            <button class="btn btn-ghost btn-sm btn-square text-error" data-remove-contact="${c.ContactId}" title="Remove contact">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <!-- Contact details (phone, email) -->
          ${contactDetails.length > 0 ? `<div class="px-3 pb-3">${contactDetails.join('')}</div>` : ''}
        </div>
      `;
    }).join('');
  }

  /**
   * Get initials from name for avatar placeholder
   */
  private static getInitials(name: string): string {
    return name.split(' ').map(n => n.charAt(0).toUpperCase()).slice(0, 2).join('');
  }

  /**
   * Render users list HTML
   */
  private static renderUsersList(users: ITaskUser[], ownerUserId: number): string {
    if (users.length === 0) {
      return '<p class="text-base-content/50 text-sm">No users assigned to this task.</p>';
    }
    
    return users.map(u => {
      const isOwner = u.UserId === ownerUserId || u.IsOwner;
      // Avatar: use photo if available, otherwise initials
      const avatarHtml = u.Avatar 
        ? `<img src="${u.Avatar}" alt="${this.escapeHtml(u.FullName)}" class="rounded-full w-10 h-10 object-cover" onerror="this.parentElement.innerHTML='<span class=\\'text-sm\\'>${this.getInitials(u.FullName)}</span>'" />`
        : `<span class="text-sm">${this.getInitials(u.FullName)}</span>`;
      
      return `
        <div class="flex items-center justify-between p-3 bg-base-200 rounded">
          <div class="flex items-center gap-3">
            <div class="avatar placeholder flex-shrink-0">
              <div class="bg-neutral text-neutral-content rounded-full w-10 h-10 flex items-center justify-center">
                ${avatarHtml}
              </div>
            </div>
            <div class="flex-1">
              <div class="font-medium">${this.escapeHtml(u.FullName)} ${isOwner ? '<span class="badge badge-xs badge-primary ml-2">Owner</span>' : ''}</div>
              <div class="text-sm text-base-content/70">${this.escapeHtml(u.LoginName)}</div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            ${!isOwner ? `<button class="btn btn-xs btn-outline btn-primary" data-set-owner="${u.UserId}">Set Owner</button>` : ''}
            <button class="btn btn-ghost btn-xs text-error" data-remove-user="${u.UserId}" title="Remove user">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      `;
    }).join('');
  }

  /**
   * Render links list HTML
   */
  private static renderLinksList(links: ITaskLink[]): string {
    if (links.length === 0) {
      return '<p class="text-base-content/50 text-sm">No entities linked to this task.</p>';
    }
    
    return links.map(l => `
      <div class="flex items-center justify-between p-2 bg-base-200 rounded">
        <div>
          <div class="text-xs font-medium text-base-content/60">${this.escapeHtml(l.Name)}</div>
          <div class="text-primary cursor-pointer">${this.escapeHtml(l.Description)}</div>
        </div>
        <button class="btn btn-ghost btn-xs text-error" data-remove-link="${l.RecordId}" data-link-type="${l.Name}">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    `).join('');
  }

  /**
   * Initialize form event handlers and components
   */
  private static async initializeForm(): Promise<void> {
    const formContent = document.getElementById('task-form-content');
    if (!formContent) {
      logger.warn('Task form content not found');
      return;
    }

    // Create component registry
    this.componentRegistry = createComponentRegistry();

    // Render all <trisys-field> elements
    const result = await renderTrisysFields(formContent, {
      registry: this.componentRegistry,
      size: 'sm'
    });
    logger.debug('Rendered task form trisys-fields', result);

    // Initialize tabs
    await this.initTabs();

    // Populate field values
    await this.populateFields();

    // Initialize AttributesAndSkills component
    await this.initAttributesAndSkills();

    // Set up event handlers
    this.setupEventHandlers();

    logger.debug('Task form initialized');
  }

  /**
   * Initialize AttributesAndSkills component for the Attributes tab
   * Uses the same reusable component as Contact form attributes
   */
  private static async initAttributesAndSkills(): Promise<void> {
    const container = document.getElementById('task-attributes-container');
    if (!container) {
      logger.warn('Task attributes container not found');
      return;
    }

    const standingData = trisysAPI.getCachedStandingData();
    if (!standingData) {
      logger.warn('Standing data not available for AttributesAndSkills');
      container.innerHTML = `
        <div class="flex items-center justify-center h-48 text-base-content/50">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <span>Standing data not available</span>
        </div>
      `;
      return;
    }

    try {
      // Get system setting for ignored categories
      const ignoreSettingValue = standingData.SystemSettings?.find(
        s => s.Name === 'Ignore Task Skill Categories'
      )?.Value || '';
      const ignoredCategories = new Set(
        ignoreSettingValue.split(';').map(c => c.trim().toLowerCase()).filter(Boolean)
      );
      // Always ignore Gender as seen in ctrlSearchCriteria.js
      ignoredCategories.add('gender');

      // Get generic skill categories (FieldId === 0)
      // These are skills that can be assigned to ANY entity (e.g., Tasks, Contacts)
      const genericCategoryIds = new Set(
        (standingData.SkillCategories || [])
          .filter(cat => cat.FieldId === 0)
          .map(cat => cat.SkillCategoryId)
      );

      // Debug: Log standing data for troubleshooting
      logger.debug('AttributesAndSkills filtering', {
        totalSkills: standingData.Skills?.length || 0,
        totalCategories: standingData.SkillCategories?.length || 0,
        genericCategoryCount: genericCategoryIds.size,
        ignoredCategories: Array.from(ignoredCategories),
        sampleSkills: standingData.Skills?.slice(0, 3).map(s => ({ 
          Skill: s.Skill, 
          FieldId: s.FieldId, 
          Category: s.Category,
          SkillCategoryId: s.SkillCategoryId 
        })),
        sampleCategories: standingData.SkillCategories?.slice(0, 3).map(c => ({
          Category: c.Category,
          FieldId: c.FieldId,
          SkillCategoryId: c.SkillCategoryId
        }))
      });

      // Available skills: in generic categories (FieldId=0), not in ignored categories
      const availableSkills: SkillItem[] = (standingData.Skills || [])
        .filter(s => 
          genericCategoryIds.has(s.SkillCategoryId) &&
          !ignoredCategories.has((s.Category || '').toLowerCase())
        )
        .map(s => ({
          skillId: s.SkillId,
          skill: s.Skill,
          description: s.Description,
          categoryId: s.SkillCategoryId,
          category: s.Category,
          ordering: s.Ordering,
          fieldId: s.FieldId,
          locked: s.Locked
        }));

      // Assigned skills: load from API if editing existing task
      let assignedSkills: SkillItem[] = [];
      if (this.currentTask && this.currentTask.TaskId > 0) {
        try {
          const skillsResponse = await trisysAPI.readEntitySkills('Task', this.currentTask.TaskId);
          if (skillsResponse.Success && skillsResponse.Skills) {
            assignedSkills = skillsResponse.Skills
              .filter(s => genericCategoryIds.has(s.SkillCategoryId))
              .map(s => ({
                skillId: s.SkillId,
                skill: s.Skill,
                description: s.Description,
                categoryId: s.SkillCategoryId,
                category: s.Category,
                ordering: s.Ordering,
                fieldId: s.FieldId,
                locked: s.Locked
              }));
          }
        } catch (error) {
          logger.error('Failed to load task skills', { error, taskId: this.currentTask.TaskId });
        }
      }

      // Destroy existing component if any
      if (this.attributesAndSkills) {
        this.attributesAndSkills.destroy();
      }

      // Create new AttributesAndSkills component
      this.attributesAndSkills = new AttributesAndSkills(container, {
        availableSkills,
        assignedSkills,
        draggable: true,
        availableTitle: 'Available Attributes',
        assignedTitle: 'Assigned Attributes',
        onAdd: (skills) => {
          logger.info('Task attributes added', { count: skills.length });
          this.isDirty = true;
        },
        onRemove: (skills) => {
          logger.info('Task attributes removed', { count: skills.length });
          this.isDirty = true;
        }
      });

      await this.attributesAndSkills.initialize();
      logger.debug('AttributesAndSkills initialized for Task', {
        availableCount: availableSkills.length,
        assignedCount: assignedSkills.length
      });

    } catch (error) {
      logger.error('Failed to initialize AttributesAndSkills', { error });
      container.innerHTML = `
        <div class="flex items-center justify-center h-48 text-error">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <span>Failed to load attributes</span>
        </div>
      `;
    }
  }

  /**
   * Save task skills/attributes to the API
   * @param taskId - The task ID to save skills for
   */
  private static async saveTaskSkills(taskId: number): Promise<void> {
    if (!taskId || !this.attributesAndSkills) return;

    const assignedSkills = this.attributesAndSkills.getAssignedSkills();
    const entitySkills: EntitySkill[] = assignedSkills.map(skill => ({
      SkillId: skill.skillId,
      Skill: skill.skill,
      Description: skill.description || skill.skill,
      Locked: skill.locked ?? false,
      SkillCategoryId: skill.categoryId,
      Category: skill.category,
      Ordering: skill.ordering,
      FieldId: skill.fieldId
    }));

    try {
      const response = await trisysAPI.writeEntitySkills('Task', taskId, entitySkills);
      if (!response.Success) {
        logger.error('Failed to save task skills', { error: response.ErrorMessage, taskId });
        ToasterService.warning('Task saved but attributes may not have been updated');
      } else {
        logger.debug('Task skills saved successfully', { taskId, skillCount: entitySkills.length });
      }
    } catch (error) {
      logger.error('Error saving task skills', { error, taskId });
      ToasterService.warning('Task saved but attributes may not have been updated');
    }
  }

  /**
   * Update modal title based on Scheduled checkbox state
   */
  private static updateModalTitle(): void {
    if (!this.modalId) return;
    
    const scheduledCheckbox = document.getElementById('task-scheduled') as HTMLInputElement;
    const isScheduled = scheduledCheckbox?.checked ?? true;
    const isEdit = this.currentTask && this.currentTask.TaskId > 0;
    
    const titleText = isScheduled ? 'Scheduled Task' : 'Note/History';
    const titleWithId = isEdit ? `${titleText} (${this.currentTask!.TaskId.toLocaleString()})` : `New ${titleText}`;
    const title = `<i class="fas fa-external-link-alt text-base-content/50 mr-2 cursor-pointer" title="Open in new window"></i>${titleWithId}`;
    
    // Update the modal title element
    const modalElement = document.querySelector(`[data-modal-id="${this.modalId}"]`) || 
                        document.getElementById(this.modalId);
    const titleElement = modalElement?.querySelector('.modal-box h3, .modal-box .text-lg');
    if (titleElement) {
      titleElement.innerHTML = title;
    }
  }

  /**
   * Initialize the tabs - using direct HTML tabs matching contact form style
   */
  private static async initTabs(): Promise<void> {
    const tabsContainer = document.getElementById('task-form-tabs');
    if (!tabsContainer) return;

    // Set up click handlers for tabs
    const tabs = tabsContainer.querySelectorAll('.task-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        const tabId = (tab as HTMLElement).dataset.tab;
        if (tabId) {
          this.switchTab(tabId);
          // Update active tab styling
          tabs.forEach(t => t.classList.remove('tab-active'));
          tab.classList.add('tab-active');
        }
      });
    });

    // Show initial tab panel
    this.switchTab('description');
  }

  /**
   * Switch visible tab panel
   */
  private static switchTab(tabId: string): void {
    // Get the modal container to scope our queries (avoid conflicts with EntityForm panels)
    const modalContainer = this.modalId 
      ? (document.querySelector(`[data-modal-id="${this.modalId}"]`) || document.getElementById(this.modalId))
      : document;
    
    if (!modalContainer) {
      logger.warn('switchTab: Modal container not found', { modalId: this.modalId });
      return;
    }
    
    // Hide all panels WITHIN the modal container
    const allPanels = modalContainer.querySelectorAll('.task-tab-panel');
    allPanels.forEach(panel => {
      panel.classList.add('hidden');
    });

    // Show selected panel WITHIN the modal container
    const panel = modalContainer.querySelector(`#panel-${tabId}`);
    if (panel) {
      panel.classList.remove('hidden');
    }

    // Update tab active state WITHIN the modal container
    modalContainer.querySelectorAll('.task-tab').forEach(tab => {
      const isActive = (tab as HTMLElement).dataset.tab === tabId;
      tab.classList.toggle('tab-active', isActive);
    });
  }

  /**
   * Set active tab by id (public helper)
   */
  private static setActiveTab(tabId: string): void {
    this.switchTab(tabId);
  }

  /**
   * Parse API date string (format: "2026/01/29 10:40") into Date object
   */
  private static parseApiDateTime(dateStr: string): Date | null {
    if (!dateStr) return null;
    
    // Handle API format "2026/01/29 10:40"
    const match = dateStr.match(/^(\d{4})\/(\d{2})\/(\d{2})\s+(\d{2}):(\d{2})$/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      return new Date(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed
        parseInt(day),
        parseInt(hours),
        parseInt(minutes)
      );
    }
    
    // Fallback to standard Date parsing (ISO format)
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Format Date to API format "YYYY/MM/DD HH:mm"
   */
  private static formatApiDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  }

  /**
   * Set date+time field value
   * Sets the DatePicker date and the hour/minute dropdowns
   * @param fieldId - Base field ID (e.g., 'task-start-date')
   * @param dateTime - Date object or API date string
   */
  private static setDateTimeFieldValue(fieldId: string, dateTime: Date | string | null): void {
    if (!dateTime) return;
    
    const date = typeof dateTime === 'string' ? this.parseApiDateTime(dateTime) : dateTime;
    if (!date || isNaN(date.getTime())) {
      logger.warn('Invalid date value for field', { fieldId, dateTime });
      return;
    }

    // Set DatePicker value (registered as Virtual_{fieldId})
    const picker = this.componentRegistry?.datePickers.get(`Virtual_${fieldId}`);
    if (picker) {
      picker.setValue(date);
    } else {
      logger.warn('DatePicker not found in registry', { fieldId, registryKeys: Array.from(this.componentRegistry?.datePickers.keys() || []) });
    }

    // Set hours dropdown
    const hoursSelect = document.getElementById(`Virtual_${fieldId}-hours`) as HTMLSelectElement;
    if (hoursSelect) {
      hoursSelect.value = String(date.getHours());
    }

    // Set minutes dropdown - round to nearest available option (handles 5-minute intervals)
    const minutesSelect = document.getElementById(`Virtual_${fieldId}-minutes`) as HTMLSelectElement;
    if (minutesSelect) {
      const actualMinutes = date.getMinutes();
      // Find the closest available option in the dropdown
      const options = Array.from(minutesSelect.options).map(opt => parseInt(opt.value, 10));
      if (options.length > 0) {
        // Find nearest option (round to closest interval)
        const closest = options.reduce((prev, curr) => 
          Math.abs(curr - actualMinutes) < Math.abs(prev - actualMinutes) ? curr : prev
        );
        minutesSelect.value = String(closest);
      } else {
        minutesSelect.value = String(actualMinutes);
      }
    }

    logger.debug('Set date+time field value', { fieldId, date: date.toISOString(), hours: date.getHours(), minutes: date.getMinutes() });
  }

  /**
   * Get date+time field value
   * Combines DatePicker date with hour/minute dropdown values
   * @param fieldId - Base field ID (e.g., 'task-start-date')
   * @returns Date object with combined date+time, or null if not set
   */
  private static getDateTimeFieldValue(fieldId: string): Date | null {
    // Get DatePicker value
    const picker = this.componentRegistry?.datePickers.get(`Virtual_${fieldId}`);
    const date = picker?.getValue();
    
    if (!date) return null;

    // Get hours from dropdown (default to 0 if not found)
    const hoursSelect = document.getElementById(`Virtual_${fieldId}-hours`) as HTMLSelectElement;
    const hours = hoursSelect ? parseInt(hoursSelect.value || '0') : 0;

    // Get minutes from dropdown (default to 0 if not found)
    const minutesSelect = document.getElementById(`Virtual_${fieldId}-minutes`) as HTMLSelectElement;
    const minutes = minutesSelect ? parseInt(minutesSelect.value || '0') : 0;

    // Combine date with time
    const result = new Date(date);
    result.setHours(hours, minutes, 0, 0);

    return result;
  }

  /**
   * Populate field values from current task
   */
  private static async populateFields(): Promise<void> {
    const task = this.currentTask;
    if (!task) return;

    // Populate Start Date + Time (use new API field name, fallback to legacy)
    const startDateTime = task.StartDate || task.StartDateTime;
    if (startDateTime) {
      this.setDateTimeFieldValue('task-start-date', startDateTime);
    }

    // Populate End Date + Time (use new API field name, fallback to legacy)
    const endDateTime = task.EndDate || task.EndDateTime;
    if (endDateTime) {
      this.setDateTimeFieldValue('task-end-date', endDateTime);
    }

    // Populate Scheduled toggle (use new API field name, fallback to legacy)
    const scheduledCheckbox = document.getElementById('task-scheduled') as HTMLInputElement;
    if (scheduledCheckbox) {
      scheduledCheckbox.checked = task.Scheduled ?? task.IsScheduled ?? false;
    }

    // Populate Alarm toggle (use new API field name, fallback to legacy)
    const alarmCheckbox = document.getElementById('task-has-alarm') as HTMLInputElement;
    const alarmLeadTimeContainer = document.getElementById('alarm-lead-time');
    if (alarmCheckbox) {
      const hasAlarm = task.Alarmed ?? task.HasAlarm ?? false;
      alarmCheckbox.checked = hasAlarm;
      if (alarmLeadTimeContainer) {
        alarmLeadTimeContainer.classList.toggle('hidden', !hasAlarm);
      }
    }

    // Populate Lead Time from API format "5 Minutes"
    if (task.LeadTime) {
      const match = task.LeadTime.match(/^(\d+)\s+(.+)$/);
      if (match) {
        const [, value, period] = match;
        const leadTimeValue = document.getElementById('task-alarm-value') as HTMLInputElement;
        const leadTimePeriod = document.getElementById('task-alarm-period') as HTMLSelectElement;
        if (leadTimeValue) leadTimeValue.value = value;
        if (leadTimePeriod) {
          // Match period (case-insensitive, singular form)
          const periodLower = period.toLowerCase().replace(/s$/, ''); // Remove trailing 's'
          const options = Array.from(leadTimePeriod.options);
          const matchingOption = options.find(o => o.value.toLowerCase().startsWith(periodLower));
          if (matchingOption) leadTimePeriod.value = matchingOption.value;
        }
      }
    }

    // Populate Attachment from Attachments array (first attachment if exists)
    const fileLink = this.componentRegistry?.fileLinks.get('Virtual_task-attachment-file');
    const attachmentDescription = document.getElementById('task-attachment-description') as HTMLTextAreaElement;
    const firstAttachment = task.Attachments?.[0];
    if (fileLink && firstAttachment?.DocumentPath) {
      fileLink.setFilePath(firstAttachment.DocumentPath);
    }
    if (attachmentDescription) {
      attachmentDescription.value = firstAttachment?.Description || '';
    }
  }

  /**
   * Set up event handlers
   */
  private static setupEventHandlers(): void {
    // Track dirty state
    const inputs = document.querySelectorAll('#task-form-content input, #task-form-content select, #task-form-content textarea');
    inputs.forEach(input => {
      input.addEventListener('change', () => { this.isDirty = true; });
      input.addEventListener('input', () => { this.isDirty = true; });
    });

    // Task type icon update
    const taskTypeSelect = document.getElementById('task-type-select') as HTMLSelectElement;
    const taskTypeIcon = document.getElementById('task-type-icon') as HTMLImageElement;
    taskTypeSelect?.addEventListener('change', () => {
      const selectedOption = taskTypeSelect.options[taskTypeSelect.selectedIndex];
      const iconUrl = selectedOption?.dataset.icon || '';
      if (taskTypeIcon) {
        taskTypeIcon.src = iconUrl;
        taskTypeIcon.style.display = iconUrl ? 'block' : 'none';
      }
    });

    // Alarm toggle - show/hide lead time controls
    const hasAlarmCheckbox = document.getElementById('task-has-alarm') as HTMLInputElement;
    const alarmLeadTime = document.getElementById('alarm-lead-time');
    hasAlarmCheckbox?.addEventListener('change', () => {
      if (alarmLeadTime) {
        alarmLeadTime.classList.toggle('hidden', !hasAlarmCheckbox.checked);
      }
    });

    // All Day Task toggle - hide end date when checked
    const allDayCheckbox = document.getElementById('task-allday') as HTMLInputElement;
    const endDateContainer = document.getElementById('end-date-container');
    allDayCheckbox?.addEventListener('change', () => {
      if (endDateContainer) {
        endDateContainer.style.display = allDayCheckbox.checked ? 'none' : '';
      }
    });

    // Scheduled toggle - update modal title
    const scheduledCheckbox = document.getElementById('task-scheduled') as HTMLInputElement;
    scheduledCheckbox?.addEventListener('change', () => {
      this.updateModalTitle();
    });

    // Date synchronization: Start Date change - if start > end, set end = start + 5 minutes
    const startDatePicker = this.componentRegistry?.datePickers.get('Virtual_task-start-date');
    const startHoursSelect = document.getElementById('Virtual_task-start-date-hours') as HTMLSelectElement;
    const startMinutesSelect = document.getElementById('Virtual_task-start-date-minutes') as HTMLSelectElement;
    
    const syncEndDateFromStart = () => {
      const start = this.getDateTimeFieldValue('task-start-date');
      const end = this.getDateTimeFieldValue('task-end-date');
      if (start && end && start >= end) {
        // Set end date to start date + 5 minutes
        const newEnd = new Date(start.getTime() + 5 * 60 * 1000);
        this.setDateTimeFieldValue('task-end-date', newEnd);
        this.isDirty = true;
      }
    };
    
    startDatePicker?.onChange(syncEndDateFromStart);
    startHoursSelect?.addEventListener('change', syncEndDateFromStart);
    startMinutesSelect?.addEventListener('change', syncEndDateFromStart);

    // Date synchronization: End Date change - if end < start, set start = end - 5 minutes
    const endDatePicker = this.componentRegistry?.datePickers.get('Virtual_task-end-date');
    const endHoursSelect = document.getElementById('Virtual_task-end-date-hours') as HTMLSelectElement;
    const endMinutesSelect = document.getElementById('Virtual_task-end-date-minutes') as HTMLSelectElement;
    
    const syncStartDateFromEnd = () => {
      const start = this.getDateTimeFieldValue('task-start-date');
      const end = this.getDateTimeFieldValue('task-end-date');
      if (start && end && end <= start) {
        // Set start date to end date - 5 minutes
        const newStart = new Date(end.getTime() - 5 * 60 * 1000);
        this.setDateTimeFieldValue('task-start-date', newStart);
        this.isDirty = true;
      }
    };
    
    endDatePicker?.onChange(syncStartDateFromEnd);
    endHoursSelect?.addEventListener('change', syncStartDateFromEnd);
    endMinutesSelect?.addEventListener('change', syncStartDateFromEnd);

    // Add contact button - opens ContactPicker modal
    document.getElementById('btn-add-contact')?.addEventListener('click', async () => {
      await this.handleAddContact();
    });

    // Remove contact buttons (using event delegation)
    document.getElementById('task-contacts-list')?.addEventListener('click', (e) => {
      const removeBtn = (e.target as HTMLElement).closest('[data-remove-contact]') as HTMLElement;
      if (removeBtn) {
        const contactId = parseInt(removeBtn.dataset.removeContact || '0');
        if (contactId) {
          this.handleRemoveContact(contactId);
        }
      }
    });

    // Add user button - opens UserPicker modal
    document.getElementById('btn-add-user')?.addEventListener('click', async () => {
      await this.handleAddUser();
    });

    // Remove user buttons (using event delegation)
    document.getElementById('task-users-list')?.addEventListener('click', (e) => {
      const removeBtn = (e.target as HTMLElement).closest('[data-remove-user]') as HTMLElement;
      if (removeBtn) {
        const userId = parseInt(removeBtn.dataset.removeUser || '0');
        if (userId) {
          this.handleRemoveUser(userId);
        }
      }
      
      // Set owner button
      const setOwnerBtn = (e.target as HTMLElement).closest('[data-set-owner]') as HTMLElement;
      if (setOwnerBtn) {
        const userId = parseInt(setOwnerBtn.dataset.setOwner || '0');
        if (userId) {
          this.handleSetOwner(userId);
        }
      }
    });

    // Link dropdown menu - add link handlers
    const linkDropdown = document.querySelector('#panel-links .dropdown-content');
    linkDropdown?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const linkItem = target.closest('[data-link-type]') as HTMLElement;
      if (linkItem) {
        const linkType = linkItem.dataset.linkType;
        if (linkType) {
          this.handleAddLink(linkType);
          // Close the dropdown by removing focus
          (document.activeElement as HTMLElement)?.blur();
        }
      }
    });

    // Remove link buttons (using event delegation)
    document.getElementById('task-links-list')?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const removeBtn = target.closest('[data-remove-link]') as HTMLElement;
      if (removeBtn) {
        e.stopPropagation(); // Prevent any parent handlers
        const recordId = parseInt(removeBtn.dataset.removeLink || '0');
        const linkType = removeBtn.dataset.linkType || '';
        if (recordId) {
          this.handleRemoveLink(recordId, linkType);
        }
      }
    });
  }

  /**
   * Handle adding a contact via ContactPicker
   */
  private static async handleAddContact(): Promise<void> {
    // Get existing contact IDs to exclude
    const existingContactIds = (this.currentTask?.Contacts || []).map(c => c.ContactId);
    
    const result = await ContactPicker.show({
      title: 'Add Task Contact',
      excludeContactIds: existingContactIds
    });
    
    if (result.selected && result.contactId) {
      // Add contact to task
      const newContact: ITaskContact = {
        ContactId: result.contactId,
        FullName: result.fullName || '',
        ContactType: (result.record?.ContactType as string) || 'Contact',
        JobTitle: (result.record?.JobTitle as string) || '',
        CompanyName: (result.record?.CompanyName as string) || '',
        ContactPhotoURL: (result.record?.ContactPhotoURL as string) || '',
        HomeAddressTelNo: (result.record?.HomeAddressTelNo as string) || '',
        MobileTelNo: (result.record?.MobileTelNo as string) || '',
        WorkTelNo: (result.record?.WorkTelNo as string) || '',
        PersonalEMail: (result.record?.PersonalEMail as string) || '',
        WorkEMail: (result.record?.WorkEMail as string) || ''
      };
      
      if (!this.currentTask!.Contacts) {
        this.currentTask!.Contacts = [];
      }
      this.currentTask!.Contacts.push(newContact);
      
      // Update UI
      this.refreshContactsList();
      this.isDirty = true;
      
      logger.debug('Contact added to task', { contactId: result.contactId, fullName: result.fullName });
    }
  }

  /**
   * Handle removing a contact
   */
  private static handleRemoveContact(contactId: number): void {
    if (!this.currentTask?.Contacts) return;
    
    this.currentTask.Contacts = this.currentTask.Contacts.filter(c => c.ContactId !== contactId);
    this.refreshContactsList();
    this.isDirty = true;
    
    logger.debug('Contact removed from task', { contactId });
  }

  /**
   * Refresh contacts list UI
   */
  private static refreshContactsList(): void {
    const contactsList = document.getElementById('task-contacts-list');
    const contactsCount = document.getElementById('contacts-count');
    
    if (contactsList) {
      contactsList.innerHTML = this.renderContactsList(this.currentTask?.Contacts || []);
    }
    if (contactsCount) {
      const count = this.currentTask?.Contacts?.length || 0;
      contactsCount.textContent = `${count} Task Contact${count !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Handle adding a user via UserPicker
   */
  private static async handleAddUser(): Promise<void> {
    // Get existing user IDs to exclude
    const existingUserIds = (this.currentTask?.Users || []).map(u => u.UserId);
    
    const result = await UserPicker.show({
      title: 'Add Task User',
      excludeUserIds: existingUserIds
    });
    
    if (result.selected && result.userId) {
      // Add user to task
      const newUser: ITaskUser = {
        UserId: result.userId,
        FullName: result.fullName || '',
        LoginName: result.loginName || '',
        IsOwner: false,
        Avatar: result.photo || null
      };
      
      if (!this.currentTask!.Users) {
        this.currentTask!.Users = [];
      }
      this.currentTask!.Users.push(newUser);
      
      // Update UI
      this.refreshUsersList();
      this.isDirty = true;
      
      logger.debug('User added to task', { userId: result.userId, fullName: result.fullName });
    }
  }

  /**
   * Handle removing a user
   */
  private static handleRemoveUser(userId: number): void {
    if (!this.currentTask?.Users) return;
    
    // Don't allow removing the owner
    const user = this.currentTask.Users.find(u => u.UserId === userId);
    if (user?.IsOwner || userId === this.currentTask.OwnerUserId) {
      ToasterService.warning('Cannot remove the task owner');
      return;
    }
    
    this.currentTask.Users = this.currentTask.Users.filter(u => u.UserId !== userId);
    this.refreshUsersList();
    this.isDirty = true;
    
    logger.debug('User removed from task', { userId });
  }

  /**
   * Handle setting a user as owner
   */
  private static handleSetOwner(userId: number): void {
    if (!this.currentTask?.Users) return;
    
    // Update OwnerUserId
    this.currentTask.OwnerUserId = userId;
    
    // Update IsOwner flags
    for (const user of this.currentTask.Users) {
      user.IsOwner = user.UserId === userId;
    }
    
    this.refreshUsersList();
    this.isDirty = true;
    
    logger.debug('User set as owner', { userId });
  }

  /**
   * Refresh users list UI
   */
  private static refreshUsersList(): void {
    const usersList = document.getElementById('task-users-list');
    const usersCount = document.getElementById('users-count');
    
    if (usersList) {
      usersList.innerHTML = this.renderUsersList(this.currentTask?.Users || [], this.currentTask?.OwnerUserId || 0);
    }
    if (usersCount) {
      const count = this.currentTask?.Users?.length || 0;
      usersCount.textContent = `${count} Task User${count !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Handle adding a link (placeholder - full implementation would open entity picker)
   */
  private static handleAddLink(linkType: string): void {
    ToasterService.info(`Link ${linkType} - Coming soon`);
    logger.debug('Add link requested', { linkType });
  }

  /**
   * Handle removing a link
   */
  private static handleRemoveLink(recordId: number, linkType: string): void {
    if (!this.currentTask?.Links) return;
    
    const index = this.currentTask.Links.findIndex(
      l => l.RecordId === recordId && l.Name === linkType
    );
    
    if (index >= 0) {
      this.currentTask.Links.splice(index, 1);
      this.refreshLinksList();
      this.isDirty = true;
      logger.debug('Link removed', { recordId, linkType });
    }
  }

  /**
   * Refresh links list UI
   */
  private static refreshLinksList(): void {
    const linksList = document.getElementById('task-links-list');
    const linksCount = document.getElementById('links-count');
    
    if (linksList) {
      linksList.innerHTML = this.renderLinksList(this.currentTask?.Links || []);
    }
    if (linksCount) {
      const count = this.currentTask?.Links?.length || 0;
      linksCount.textContent = `${count} Task Link${count !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Collect form data into a task object
   */
  private static collectFormData(): ITask {
    const task = { ...this.currentTask! };
    
    // Task Type
    const taskTypeSelect = document.getElementById('task-type-select') as HTMLSelectElement;
    task.TaskTypeId = parseInt(taskTypeSelect?.value || '1');
    task.TaskTypeName = taskTypeSelect?.options[taskTypeSelect.selectedIndex]?.text || '';
    task.TaskType = task.TaskTypeName; // API uses TaskType string

    // Scheduled (set both new and legacy field names)
    const isScheduled = (document.getElementById('task-scheduled') as HTMLInputElement)?.checked || false;
    task.Scheduled = isScheduled;
    task.IsScheduled = isScheduled;

    // All Day Task
    task.IsAllDayTask = (document.getElementById('task-allday') as HTMLInputElement)?.checked || false;

    // Start DateTime - use new helper method
    const startDate = this.getDateTimeFieldValue('task-start-date');
    if (startDate) {
      task.StartDate = this.formatApiDateTime(startDate);
      task.StartDateTime = startDate.toISOString(); // Legacy format
    }

    // End DateTime - use new helper method
    const endDate = this.getDateTimeFieldValue('task-end-date');
    if (endDate) {
      task.EndDate = this.formatApiDateTime(endDate);
      task.EndDateTime = endDate.toISOString(); // Legacy format
    }

    // Description
    task.Description = (document.getElementById('task-description') as HTMLTextAreaElement)?.value || '';

    // Alarm settings (set both new and legacy field names)
    const hasAlarm = (document.getElementById('task-has-alarm') as HTMLInputElement)?.checked || false;
    task.Alarmed = hasAlarm;
    task.HasAlarm = hasAlarm;
    
    // Lead Time - combine value and period for API format "5 minutes"
    const leadTimeValue = parseInt((document.getElementById('task-alarm-value') as HTMLInputElement)?.value || '15');
    const leadTimePeriod = (document.getElementById('task-alarm-period') as HTMLSelectElement)?.value || 'Minutes';
    task.LeadTime = `${leadTimeValue} ${leadTimePeriod}`;
    task.AlarmLeadTime = leadTimeValue; // Legacy
    task.AlarmLeadTimePeriod = leadTimePeriod.toLowerCase(); // Legacy

    // Attachments: API expects array of { DocumentPath, Description }
    const fileLink = this.componentRegistry?.fileLinks.get('Virtual_task-attachment-file');
    const attachmentDescription = document.getElementById('task-attachment-description') as HTMLTextAreaElement;
    const filePath = fileLink?.getFilePath();
    if (filePath) {
      task.Attachments = [{
        DocumentPath: filePath,
        Description: attachmentDescription?.value || ''
      }];
    } else {
      task.Attachments = undefined;
    }
    // Remove legacy fields - API only uses Attachments array
    delete task.AttachmentFilePath;
    delete task.AttachmentDescription;

    // Contacts: API expects array of { ContactId } - use type assertion for API format
    if (task.Contacts && task.Contacts.length > 0) {
      task.Contacts = task.Contacts.map(c => ({ ContactId: c.ContactId })) as ITaskContact[];
    }

    // Users: API expects array of { UserId } - use type assertion for API format
    if (task.Users && task.Users.length > 0) {
      task.Users = task.Users.map(u => ({ UserId: u.UserId })) as ITaskUser[];
    }

    // Skills: get from AttributesAndSkills component (API expects full skill objects)
    if (this.attributesAndSkills) {
      const assignedSkills = this.attributesAndSkills.getAssignedSkills();
      task.Skills = assignedSkills.length > 0 
        ? assignedSkills.map(skill => ({
            SkillId: skill.skillId,
            Skill: skill.skill,
            Description: skill.description || skill.skill,
            Locked: skill.locked || false,
            SkillCategoryId: skill.categoryId,
            Category: skill.category,
            Ordering: skill.ordering,
            FieldId: skill.fieldId
          }))
        : null;
    }

    return task;
  }

  /**
   * Validate the form
   */
  private static validateForm(): boolean {
    // Description is required
    const description = (document.getElementById('task-description') as HTMLTextAreaElement)?.value?.trim() || '';
    if (!description) {
      ToasterService.warning('Description is required');
      this.setActiveTab('description');
      document.getElementById('task-description')?.focus();
      return false;
    }

    const startDate = this.getDateTimeFieldValue('task-start-date');
    const endDate = this.getDateTimeFieldValue('task-end-date');

    if (!startDate) {
      ToasterService.warning('Start Date/Time is required');
      this.setActiveTab('description');
      return false;
    }

    // Only validate end date if not an all-day task
    const isAllDay = (document.getElementById('task-allday') as HTMLInputElement)?.checked || false;
    if (!isAllDay && !endDate) {
      ToasterService.warning('End Date/Time is required');
      this.setActiveTab('description');
      return false;
    }

    if (endDate && endDate < startDate) {
      ToasterService.warning('End Date/Time must be after Start Date/Time');
      return false;
    }

    return true;
  }

  /**
   * Handle save action
   */
  private static async handleSave(closeAfter: boolean): Promise<void> {
    if (!this.validateForm()) return;

    const task = this.collectFormData();
    const isNew = task.TaskId === 0;
    
    // Mark as not dirty since we're about to save
    this.isDirty = false;

    // Close the form immediately if requested - don't make user wait for API
    if (closeAfter) {
      this.closeForm();
    }

    // Save in background
    try {
      // Use /Task/Write for both create and update, wrapped in { Task: ..., DiaryTask: true }
      const requestBody = { Task: task, DiaryTask: true };
      const response = await trisysAPI.request<{ Success: boolean; Task?: ITask; ErrorMessage?: string }>('/Task/Write', requestBody);

      if (response.Success) {
        if (response.Task?.TaskId) {
          task.TaskId = response.Task.TaskId;
        }
        
        // Dispatch global event for any listening forms (e.g., DiaryManager, ContactDetail)
        window.dispatchEvent(new CustomEvent('flexiva:task-saved', { 
          detail: { task, isNew } 
        }));
        
        if (this.options.onSave) {
          this.options.onSave(task);
        }
      } else {
        const errorMsg = response.ErrorMessage || 'Failed to save task';
        logger.error('Task save failed', { errorMessage: errorMsg, task });
        ToasterService.error(errorMsg);
      }
    } catch (error) {
      logger.error('Failed to save task', { error, task });
      ToasterService.error('Failed to save task');
    }
  }

  /**
   * Handle Save & Clear action
   */
  private static async handleSaveAndClear(): Promise<void> {
    if (!this.validateForm()) return;

    const task = this.collectFormData();
    const isNew = task.TaskId === 0;

    try {
      // Use /Task/Write for both create and update
      const requestBody = { Task: task, DiaryTask: true };
      const response = await trisysAPI.request<{ Success: boolean; Task?: ITask; ErrorMessage?: string }>('/Task/Write', requestBody);

      if (response.Success) {
        ToasterService.success(isNew ? 'Task created' : 'Task saved');
        this.isDirty = false;
        
        // Dispatch global event for any listening forms (e.g., DiaryManager)
        window.dispatchEvent(new CustomEvent('flexiva:task-saved', { 
          detail: { task, isNew } 
        }));
        
        if (this.options.onSave) {
          this.options.onSave(task);
        }

        // Clear for new entry
        this.currentTask = this.createNewTask(this.options.scheduled !== false);
        await this.refreshForm();
      } else {
        const errorMsg = response.ErrorMessage || 'Failed to save task';
        logger.error('Save and clear failed', { errorMessage: errorMsg, task });
        ToasterService.error(errorMsg);
      }
    } catch (error) {
      logger.error('Failed to save task', { error, task });
      ToasterService.error('Failed to save task');
    }
  }

  /**
   * Handle Follow-Up action
   */
  private static async handleFollowUp(): Promise<void> {
    if (!this.validateForm()) return;

    const task = this.collectFormData();
    const isNew = task.TaskId === 0;

    try {
      // Save current task first using /Task/Write
      const requestBody = { Task: task, DiaryTask: true };
      const response = await trisysAPI.request<{ Success: boolean; Task?: ITask; ErrorMessage?: string }>('/Task/Write', requestBody);

      if (response.Success) {
        ToasterService.success('Task saved');
        
        // Dispatch global event for any listening forms (e.g., DiaryManager)
        window.dispatchEvent(new CustomEvent('flexiva:task-saved', { 
          detail: { task, isNew } 
        }));
        
        if (this.options.onSave) {
          this.options.onSave(task);
        }

        // Create follow-up task with same contacts, users, links
        // Start date = current task's end date
        const followUpStartDate = task.EndDate || task.EndDateTime;
        const followUpStartDateObj = followUpStartDate ? this.parseApiDateTime(followUpStartDate) || new Date() : new Date();
        const followUpEndDateObj = new Date(followUpStartDateObj.getTime() + 5 * 60 * 1000);
        
        this.currentTask = {
          ...this.createNewTask(this.options.scheduled !== false),
          StartDate: this.formatApiDateTime(followUpStartDateObj),
          EndDate: this.formatApiDateTime(followUpEndDateObj),
          StartDateTime: followUpStartDateObj.toISOString(),
          EndDateTime: followUpEndDateObj.toISOString(),
          Contacts: task.Contacts,
          Users: task.Users,
          Links: task.Links
        };
        
        this.isDirty = false;
        await this.refreshForm();
        ToasterService.info('Follow-up task created - ready to edit');
      } else {
        const errorMsg = response.ErrorMessage || 'Failed to save task';
        logger.error('Follow-up save failed', { errorMessage: errorMsg, task });
        ToasterService.error(errorMsg);
      }
    } catch (error) {
      logger.error('Failed to save task', { error, task });
      ToasterService.error('Failed to save task');
    }
  }

  /**
   * Refresh the form with current task data
   */
  private static async refreshForm(): Promise<void> {
    // Re-populate fields with new task data
    await this.populateFields();

    // Clear description
    const descTextarea = document.getElementById('task-description') as HTMLTextAreaElement;
    if (descTextarea) {
      descTextarea.value = this.currentTask?.Description || '';
    }

    // Update contacts/users/links lists
    this.refreshContactsList();
    this.refreshUsersList();
    
    const linksList = document.getElementById('task-links-list');
    if (linksList) {
      linksList.innerHTML = this.renderLinksList(this.currentTask?.Links || []);
    }

    // Reinitialize attributes for new task (will be empty for new tasks)
    await this.initAttributesAndSkills();

    // Switch to description tab
    this.setActiveTab('description');
  }

  /**
   * Handle delete action
   */
  private static handleDelete(): void {
    if (!this.currentTask || this.currentTask.TaskId === 0) return;

    const taskId = this.currentTask.TaskId;
    const taskType = this.options.scheduled !== false ? 'task' : 'note';

    ModalSystem.questionYesNo(
      `Are you sure you want to delete this ${taskType}?`,
      'Confirm Delete',
      async () => {
        try {
          const response = await trisysAPI.request<{ Success: boolean; ErrorMessage?: string }>('/Task/Delete', { TaskId: taskId });
          
          if (response.Success) {
            ToasterService.success(`${taskType.charAt(0).toUpperCase() + taskType.slice(1)} deleted`);
            
            if (this.options.onDelete) {
              this.options.onDelete(taskId);
            }

            this.closeForm();
          } else {
            const errorMsg = response.ErrorMessage || `Failed to delete ${taskType}`;
            logger.error('Delete task failed', { errorMessage: errorMsg, taskId, taskType });
            ToasterService.error(errorMsg);
          }
        } catch (error) {
          logger.error('Failed to delete task', { error, taskId });
          ToasterService.error(`Failed to delete ${taskType}`);
        }
      },
      () => {} // No action on cancel
    );
  }

  /**
   * Handle close action
   */
  private static handleClose(): void {
    if (this.isDirty) {
      ModalSystem.questionYesNo(
        'You have unsaved changes. Are you sure you want to close?',
        'Unsaved Changes',
        () => {
          this.closeForm();
        },
        () => {} // Stay open
      );
    } else {
      this.closeForm();
    }
  }

  /**
   * Close the form and clean up
   */
  private static closeForm(): void {
    // Destroy AttributesAndSkills component
    if (this.attributesAndSkills) {
      this.attributesAndSkills.destroy();
      this.attributesAndSkills = null;
    }

    // Destroy component registry
    if (this.componentRegistry) {
      destroyComponentRegistry(this.componentRegistry);
      this.componentRegistry = null;
    }

    // Close modal
    if (this.modalId) {
      ModalSystem.hide(this.modalId);
      this.modalId = null;
    }

    // Fire callback
    if (this.options.onClose) {
      this.options.onClose();
    }
  }

  /**
   * Escape HTML entities
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
